import React, { useMemo, useState } from 'react';
import { RootineDataGrid } from '../../atoms/RootineDataGrid';
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import { useMe, useNutrients } from '../../config/api/queries';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { UserNutrientSummary } from '../../types';
import { Loading } from '../Layout';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors, theme } from '../../utils/theme';
import { RootineOutlineChip } from '../../atoms/RootineOutlineChip';
import benefitsData from '../../config/post_quiz_nutrients_types.json';
import Image from 'mui-image';
import RootineButton from '../../atoms/RootineButton';
import { RootineTooltip } from '../../atoms/RootineTooltip';
import {
  CHECKOUT_URLS,
  PRODUCT_SLUG_MAPPING,
  ROOTINE_WEB_URL,
} from '../../config';
import { ReactComponent as HelpIcon } from '../../images/help-circle.svg';
import { alpha } from '@mui/system';
import { NutrientsDataEmptyState } from './NutrientsDataEmptyState';
import { NutrientCard } from '../Results';

interface BenefitData {
  nutrient: string;
  benefits: string[];
  imgSrc: string;
}

interface NutrientTableRow {
  id: string;
  nutrient: string;
  dosage: string;
  personalization: string;
}

interface RowClickParams {
  row: NutrientTableRow;
}

const MOBILE_COLUMNS = {
  nutrient: true,
  dosage: false,
  personalization: false,
  symptoms: false,
};

const TABLET_COLUMNS = {
  nutrient: true,
  dosage: true,
  personalization: true,
  symptoms: false,
};

const ALL_COLUMNS = {
  nutrient: true,
  dosage: true,
  personalization: true,
  symptoms: true,
};

const styles = {
  img: { borderRadius: 50 },
  helpIcon: { marginRight: 24, cursor: 'pointer' },
};

const benefitsDataMap: Record<string, BenefitData> = {};
benefitsData.forEach((item) => {
  benefitsDataMap[item.nutrient] = item;
});

export const NutrientsSection = ({
  isTableOnly = false,
  dataGridHeight,
}: {
  isTableOnly?: boolean;
  dataGridHeight?: number;
}): JSX.Element => {
  const nutrients = useNutrients();
  const me = useMe();
  const isMobile = useMediaQuery(theme.breakpoints.down(520));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.between(900, 1200));
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedNutrient, setSelectedNutrient] =
    useState<UserNutrientSummary>();
  const handleModalClose = () => setModalOpen(false);
  const columnVisible = isTablet
    ? MOBILE_COLUMNS
    : isLaptop
    ? TABLET_COLUMNS
    : ALL_COLUMNS;

  const filterNutrients = (nutrients: UserNutrientSummary[]) => {
    let DO_NOT_SHOW = ['Vitamin A'];
    if (!me.data?.omega3_status) {
      DO_NOT_SHOW.push('Omega 3');
    }
    return nutrients.filter(
      (nutrient) => nutrient.title && !DO_NOT_SHOW.includes(nutrient.title)
    );
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'nutrient',
        headerName: isTablet ? 'Formula' : 'Name',
        flex: 0.3,
        maxWidth: isTablet ? 450 : 330,
        renderHeader: (params: GridColumnHeaderParams) => (
          <Box sx={{ ml: 2 }}>{params.colDef.headerName}</Box>
        ),
        renderCell: ({ row }) => {
          const nutrientData = benefitsDataMap[row.nutrient];
          return (
            <>
              {isTablet ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ width: '100%' }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography
                        fontSize={14}
                        variant="mono"
                        sx={{
                          ml: { md: 1.5 },
                          mb: 0.5,
                          color: colors.greys.GREY_500,
                        }}
                      >
                        Micronutrient
                      </Typography>
                      <Typography
                        fontSize={16}
                        variant={isMobile ? 'bold' : undefined}
                      >
                        {row.nutrient}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                    >
                      <Typography
                        fontSize={14}
                        variant="mono"
                        sx={{
                          ml: { md: 1.5 },
                          mb: 0.5,
                          color: colors.greys.GREY_500,
                        }}
                      >
                        Your Dose
                      </Typography>
                      <Typography
                        fontSize={16}
                        variant={isMobile ? 'bold' : undefined}
                      >
                        {row.dosage}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography
                      fontSize={14}
                      variant="mono"
                      sx={{
                        ml: { md: 1.5 },
                        mt: 2,
                        mb: 0.5,
                        color: colors.greys.GREY_500,
                      }}
                    >
                      Based on:
                    </Typography>
                    <Typography
                      fontSize={16}
                      variant={isMobile ? 'bold' : undefined}
                    >
                      {row.personalization}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  sx={{ ml: 2 }}
                >
                  {nutrientData?.imgSrc && (
                    <Image
                      src={nutrientData.imgSrc}
                      width={40}
                      height={40}
                      style={styles.img}
                    />
                  )}

                  <Typography fontSize={14} sx={{ ml: { md: 1.5 } }}>
                    {row.nutrient}
                  </Typography>
                </Box>
              )}
            </>
          );
        },
      },
      {
        field: 'dosage',
        headerName: 'Your Dose',
        flex: 0.2,
        maxWidth: isMobile || isTablet ? 115 : 160,
        renderHeader: () => (
          <Grid
            container
            flexDirection="row"
            alignItems="center"
            sx={{ ml: 2 }}
          >
            <Typography fontSize={14} sx={{ mr: 0.5, fontWeight: 500 }}>
              Your Dose
            </Typography>

            {!isMobile && (
              <RootineTooltip
                tooltipProps={{
                  title:
                    'Your daily dosage is personalized based on the health information you give Rootine',
                  placement: 'top',
                  children: <HelpIcon style={styles.helpIcon} />,
                }}
              />
            )}
          </Grid>
        ),
        renderCell: ({ row }) => (
          <Typography fontSize={14} sx={{ ml: 2 }}>
            {row.dosage}
          </Typography>
        ),
      },
      {
        field: 'personalization',
        flex: 0.2,
        maxWidth: 200,
        sortable: false,
        renderHeader: () => (
          <Grid
            container
            flexDirection="row"
            alignItems="center"
            sx={{ ml: 2 }}
          >
            <Typography fontSize={14} sx={{ mr: 0.5 }}>
              Dosage Based On
            </Typography>

            <RootineTooltip
              tooltipProps={{
                title:
                  'Your formulation is personalized based on a lifestyle assessment, genetics at-home test, and blood at-home test.\n\n As you complete at-home tests, your smart multivitamin will automatically update your dosages as needed.\n\nYour formulation will also automatically update, for example, when you get older.',
                placement: 'top',
                children: <HelpIcon style={styles.helpIcon} />,
              }}
            />
          </Grid>
        ),
        renderCell: ({ row }) => {
          const personalizationData = row.personalization.toLowerCase();
          const basedOnArray = ['lifestyle', 'blood', 'dna', 'cortisol'];
          return (
            <FormGroup sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
              {basedOnArray.map((text: string, index: number) => (
                <FormControlLabel
                  key={`${row.nutrient}-${index}`}
                  control={
                    <Checkbox
                      size="small"
                      icon={
                        <RadioButtonUncheckedIcon
                          sx={{
                            width: 16,
                            height: 16,
                            color: colors.greys.GREY_550,
                          }}
                        />
                      }
                      checkedIcon={
                        <CheckCircleOutlineIcon
                          sx={{
                            width: 16,
                            height: 16,
                            background: colors.greens.HIGLIGHT,
                            borderRadius: 50,
                            boxSizing: 'border-box',
                          }}
                        />
                      }
                      sx={{
                        padding: 0,
                      }}
                    />
                  }
                  label={text.includes('dna') ? text.toUpperCase() : text}
                  checked={personalizationData.includes(text)}
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontWeight: 500,
                      color: colors.greys.BRAND_BLACK,
                      ml: 1,
                      textTransform: 'capitalize',
                    },
                    '& .MuiTypography-root': {
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.greys.BRAND_BLACK,
                    },
                  }}
                />
              ))}
            </FormGroup>
          );
        },
      },
      {
        field: 'symptoms',
        headerName: 'Nutrient Benefits',
        flex: 0.2,
        maxWidth: isTableOnly ? 380 : 350,
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => (
          <Box sx={{ ml: 2 }}>{params.colDef.headerName}</Box>
        ),
        renderCell: ({ row }) => {
          const nutrientData = benefitsDataMap[row.nutrient];
          const chipColors = [
            {
              bgColor: 'rgba(175, 240, 123, 0.30)',
              borderColor: colors.greens.MID,
              fontColor: colors.greens.BASE,
            },
            {
              bgColor: 'rgba(215, 216, 210, 0.30)',
              borderColor: colors.greys.GREY_900,
              fontColor: colors.greens.BASE,
            },
            {
              bgColor: 'rgba(0, 117, 255, 0.30)',
              borderColor: colors.blues.MID,
              fontColor: colors.blues.BRIGHT,
            },
          ];
          return (
            <Grid
              container
              flexDirection="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ ml: 2 }}
            >
              {nutrientData?.benefits.map((benefit, index) => (
                <RootineOutlineChip
                  key={index}
                  chipProps={{ label: benefit }}
                  colorOverrides={{
                    backgroundColor:
                      chipColors[index % chipColors.length].bgColor,
                    borderColor:
                      chipColors[index % chipColors.length].borderColor,
                    fontColor: chipColors[index % chipColors.length].fontColor,
                  }}
                />
              ))}
            </Grid>
          );
        },
      },
    ];
  }, [isMobile, isTablet, me, nutrients]);

  if (me.isLoading || nutrients.isLoading) {
    return <Loading />;
  }

  if (me.isError || nutrients.isError) {
    console.log('Error fetching nutrients');
    return <></>;
  }

  const filteredNutrients = filterNutrients(nutrients.data);

  const rows = filteredNutrients.map((nutrient) => ({
    id: nutrient.nutrient_id,
    nutrient: nutrient.title,
    dosage: nutrient.value_mg,
    personalization: nutrient.based_on,
  }));

  const userResultText = () => {
    if (me.data.blood_result && me.data.dna_result) {
      return 'Heavy';
    } else if (me.data.blood_result || me.data.dna_result) {
      return 'Medium';
    } else {
      return 'Light';
    }
  };

  const handleRowClick = (params: RowClickParams) => {
    const selectedNutrientDetail = filteredNutrients.find(
      (nutrient) => nutrient.nutrient_id === params.row?.id
    );
    setSelectedNutrient(selectedNutrientDetail);
    setModalOpen(true);
  };

  return (
    <>
      {!isTableOnly ? (
        <Grid item>
          <Grid
            container
            flexDirection="column"
            wrap="nowrap"
            justifyContent="center"
            sx={{
              py: 12,
              px: { xs: 3, md: 10, lg: 14 },
            }}
          >
            <Grid container item alignItems="center">
              <Typography
                fontSize={16}
                sx={{ textTransform: 'uppercase', mr: 1, wordSpacing: 2 }}
                id="smart-multi-formulation"
              >
                Personalization level:
              </Typography>
              <Typography
                fontSize={16}
                sx={{ textTransform: 'uppercase', mr: 0.5 }}
                id="smart-multi-formulation"
              >
                {userResultText()}
              </Typography>
              <RootineTooltip
                tooltipProps={{
                  title: (
                    <Grid container flexDirection="column">
                      <Grid
                        container
                        item
                        flexDirection="row"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="bold"
                          fontSize={14}
                          sx={{ mr: 0.5 }}
                        >
                          Light:
                        </Typography>
                        <Typography variant="light" fontSize={14}>
                          Lifestyle Quiz Completed
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        flexDirection="row"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="bold"
                          fontSize={14}
                          sx={{ mr: 0.5 }}
                        >
                          Medium:
                        </Typography>
                        <Typography variant="light" fontSize={14}>
                          Lifestyle Quiz & Nutrient Test Completed
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        flexDirection="row"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="bold"
                          fontSize={14}
                          sx={{ mr: 0.5 }}
                        >
                          Heavy:
                        </Typography>
                        <Typography variant="light" fontSize={14}>
                          Lifestyle Quiz & All Nutrient Test Completed
                        </Typography>
                      </Grid>
                    </Grid>
                  ),
                  placement: 'top',
                  children: <HelpIcon style={styles.helpIcon} />,
                  sx: { width: 'max-content' },
                }}
              />
            </Grid>

            <Typography
              variant="medium"
              sx={{ mt: 2, mb: 5, fontSize: { xs: 32, md: 40 } }}
            >
              {me?.data?.first_name}'s Smart Multivitamin Formulation includes:
            </Typography>

            <Grid item xs={12}>
              <Grid
                container
                flexDirection="row"
                alignItems="center"
                sx={{
                  px: 3,
                  py: 2.5,
                  borderBottom: 'none',
                  border: `1px solid ${colors.greys.BORDER_GREY}`,
                  borderTopLeftRadius: '5px',
                  borderTopRightRadius: '5px',
                  background: colors.greys.BASE_WHITE,
                }}
              >
                <Typography fontSize={18} sx={{ mr: 1, fontWeight: 500 }}>
                  Vitamins and Minerals
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${alpha(colors.greens.MID, 1)}`,
                    borderRadius: 50,
                    py: 0.25,
                    px: 1,
                    background: alpha(colors.greens.HIGLIGHT, 0.3),
                  }}
                >
                  <Typography fontSize={12} sx={{ fontWeight: 500 }}>
                    {filteredNutrients?.length}
                  </Typography>
                </Box>
              </Grid>

              <RootineDataGrid
                dataGridProps={{
                  columns: columns,
                  rows: rows,
                  hideFooter: true,
                  rowHeight: isTablet ? 140 : 100,
                  rowSelection: false,
                  columnVisibilityModel: {
                    ...columnVisible,
                  },
                  disableColumnMenu: true,
                  slots: {
                    noRowsOverlay: NutrientsDataEmptyState,
                  },
                  autoHeight: dataGridHeight ? false : true,
                }}
                hasCustomHeader
                boxStyles={{ height: dataGridHeight }}
              />
            </Grid>
            <Grid container item flexDirection="column">
              <Typography fontSize={24} variant="light" sx={{ my: 5 }}>
                Level up your personalization with one (or all) of our at-home
                lab tests. Take control of your health by discovering what your
                body truly needs.
              </Typography>

              <Grid container flexDirection={isTablet ? 'column' : 'row'}>
                <RootineButton
                  variation="primary"
                  size="large"
                  url={CHECKOUT_URLS.vitamins.link}
                  label="No thanks. Continue to checkout"
                  sx={{
                    mr: 3,
                    [theme.breakpoints.down('laptop')]: {
                      mb: 3,
                    },
                  }}
                  fullWidth={isTablet}
                  as="a"
                  openNewTab
                />
                <RootineButton
                  variation="outline_light"
                  size="large"
                  url={`${ROOTINE_WEB_URL}/collections/${PRODUCT_SLUG_MAPPING.at_home_test_collection}`}
                  hideIcon
                  label="Add at-home Vitamin and Mineral test"
                  fullWidth={isTablet}
                  as="a"
                  openNewTab
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <RootineDataGrid
            dataGridProps={{
              columns: columns,
              rows: rows,
              hideFooter: true,
              rowHeight: isTablet ? 140 : 100,
              rowSelection: false,
              columnVisibilityModel: {
                ...columnVisible,
              },
              disableColumnMenu: true,
              slots: {
                noRowsOverlay: NutrientsDataEmptyState,
              },
              onRowClick: handleRowClick,
              autoHeight: dataGridHeight ? false : true,
            }}
            hasCustomHeader={false}
            boxStyles={{ cursor: 'pointer', height: dataGridHeight }}
          />

          <Modal
            open={modalOpen}
            onClose={handleModalClose}
            aria-labelledby="modal-nutrient-detail"
            aria-describedby="modal-information-about-nutrient"
            sx={{
              overflow: 'scroll',
            }}
          >
            <Box>
              <NutrientCard
                nutrient={selectedNutrient}
                onClose={handleModalClose}
              />
            </Box>
          </Modal>
        </Box>
      )}
    </>
  );
};
